// F40 — Setup i18next para el frontend. Carga estática de los namespaces ES,
// EN y PT desde /src/locales. No usamos backend remoto: los JSON viajan en el
// bundle (~3.3k claves x 3 idiomas son ~150KB gz, aceptable). Si esto creciera
// podríamos dividir por route con i18next-http-backend.
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import esCommon from './locales/es/common.json'
import esEnums from './locales/es/enums.json'
import esAuth from './locales/es/auth.json'
import esDashboard from './locales/es/dashboard.json'
import esClients from './locales/es/clients.json'
import esOpportunities from './locales/es/opportunities.json'
import esProjects from './locales/es/projects.json'
import esQuotes from './locales/es/quotes.json'
import esBilling from './locales/es/billing.json'
import esAccounting from './locales/es/accounting.json'
import esBanking from './locales/es/banking.json'
import esAssets from './locales/es/assets.json'
import esAnnual from './locales/es/annual.json'
import esBudget from './locales/es/budget.json'
import esExpenses from './locales/es/expenses.json'
import esExpenseSheets from './locales/es/expenseSheets.json'
import esTasks from './locales/es/tasks.json'
import esAgenda from './locales/es/agenda.json'
import esDocuments from './locales/es/documents.json'
import esRates from './locales/es/rates.json'
import esAi from './locales/es/ai.json'
import esSettings from './locales/es/settings.json'
import esNotifications from './locales/es/notifications.json'
import esEmail from './locales/es/email.json'
import esTime from './locales/es/time.json'
import esProducts from './locales/es/products.json'
import esCases from './locales/es/cases.json'
import esMarketing from './locales/es/marketing.json'

import enCommon from './locales/en/common.json'
import enEnums from './locales/en/enums.json'
import enAuth from './locales/en/auth.json'
import enDashboard from './locales/en/dashboard.json'
import enClients from './locales/en/clients.json'
import enOpportunities from './locales/en/opportunities.json'
import enProjects from './locales/en/projects.json'
import enQuotes from './locales/en/quotes.json'
import enBilling from './locales/en/billing.json'
import enAccounting from './locales/en/accounting.json'
import enBanking from './locales/en/banking.json'
import enAssets from './locales/en/assets.json'
import enAnnual from './locales/en/annual.json'
import enBudget from './locales/en/budget.json'
import enExpenses from './locales/en/expenses.json'
import enExpenseSheets from './locales/en/expenseSheets.json'
import enTasks from './locales/en/tasks.json'
import enAgenda from './locales/en/agenda.json'
import enDocuments from './locales/en/documents.json'
import enRates from './locales/en/rates.json'
import enAi from './locales/en/ai.json'
import enSettings from './locales/en/settings.json'
import enNotifications from './locales/en/notifications.json'
import enEmail from './locales/en/email.json'
import enTime from './locales/en/time.json'
import enProducts from './locales/en/products.json'
import enCases from './locales/en/cases.json'
import enMarketing from './locales/en/marketing.json'

import ptCommon from './locales/pt/common.json'
import ptEnums from './locales/pt/enums.json'
import ptAuth from './locales/pt/auth.json'
import ptDashboard from './locales/pt/dashboard.json'
import ptClients from './locales/pt/clients.json'
import ptOpportunities from './locales/pt/opportunities.json'
import ptProjects from './locales/pt/projects.json'
import ptQuotes from './locales/pt/quotes.json'
import ptBilling from './locales/pt/billing.json'
import ptAccounting from './locales/pt/accounting.json'
import ptBanking from './locales/pt/banking.json'
import ptAssets from './locales/pt/assets.json'
import ptAnnual from './locales/pt/annual.json'
import ptBudget from './locales/pt/budget.json'
import ptExpenses from './locales/pt/expenses.json'
import ptExpenseSheets from './locales/pt/expenseSheets.json'
import ptTasks from './locales/pt/tasks.json'
import ptAgenda from './locales/pt/agenda.json'
import ptDocuments from './locales/pt/documents.json'
import ptRates from './locales/pt/rates.json'
import ptAi from './locales/pt/ai.json'
import ptSettings from './locales/pt/settings.json'
import ptNotifications from './locales/pt/notifications.json'
import ptEmail from './locales/pt/email.json'
import ptTime from './locales/pt/time.json'
import ptProducts from './locales/pt/products.json'
import ptCases from './locales/pt/cases.json'
import ptMarketing from './locales/pt/marketing.json'

export const SUPPORTED_LOCALES = ['es', 'en', 'pt'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]
export const DEFAULT_LOCALE: SupportedLocale = 'es'

// Mapeo entre el enum UserLocale del backend y el código BCP-47 de i18next.
export function userLocaleToTag(value: string | null | undefined): SupportedLocale {
  if (!value) return DEFAULT_LOCALE
  const lower = value.toLowerCase()
  if (SUPPORTED_LOCALES.includes(lower as SupportedLocale)) return lower as SupportedLocale
  return DEFAULT_LOCALE
}

export function tagToUserLocale(value: SupportedLocale): 'ES' | 'EN' | 'PT' {
  return value.toUpperCase() as 'ES' | 'EN' | 'PT'
}

export const NAMESPACES = [
  'common',
  'enums',
  'auth',
  'dashboard',
  'clients',
  'opportunities',
  'cases',
  'projects',
  'quotes',
  'billing',
  'accounting',
  'banking',
  'assets',
  'annual',
  'budget',
  'expenses',
  'expenseSheets',
  'tasks',
  'agenda',
  'documents',
  'rates',
  'ai',
  'settings',
  'notifications',
  'email',
  'time',
  'products',
  'marketing',
] as const

const resources = {
  es: {
    common: esCommon,
    enums: esEnums,
    auth: esAuth,
    dashboard: esDashboard,
    clients: esClients,
    opportunities: esOpportunities,
    cases: esCases,
    marketing: esMarketing,
    projects: esProjects,
    quotes: esQuotes,
    billing: esBilling,
    accounting: esAccounting,
    banking: esBanking,
    assets: esAssets,
    annual: esAnnual,
    budget: esBudget,
    expenses: esExpenses,
    expenseSheets: esExpenseSheets,
    tasks: esTasks,
    agenda: esAgenda,
    documents: esDocuments,
    rates: esRates,
    ai: esAi,
    settings: esSettings,
    notifications: esNotifications,
    email: esEmail,
    time: esTime,
    products: esProducts,
  },
  en: {
    common: enCommon,
    enums: enEnums,
    auth: enAuth,
    dashboard: enDashboard,
    clients: enClients,
    opportunities: enOpportunities,
    cases: enCases,
    marketing: enMarketing,
    projects: enProjects,
    quotes: enQuotes,
    billing: enBilling,
    accounting: enAccounting,
    banking: enBanking,
    assets: enAssets,
    annual: enAnnual,
    budget: enBudget,
    expenses: enExpenses,
    expenseSheets: enExpenseSheets,
    tasks: enTasks,
    agenda: enAgenda,
    documents: enDocuments,
    rates: enRates,
    ai: enAi,
    settings: enSettings,
    notifications: enNotifications,
    email: enEmail,
    time: enTime,
    products: enProducts,
  },
  pt: {
    common: ptCommon,
    enums: ptEnums,
    auth: ptAuth,
    dashboard: ptDashboard,
    clients: ptClients,
    opportunities: ptOpportunities,
    cases: ptCases,
    marketing: ptMarketing,
    projects: ptProjects,
    quotes: ptQuotes,
    billing: ptBilling,
    accounting: ptAccounting,
    banking: ptBanking,
    assets: ptAssets,
    annual: ptAnnual,
    budget: ptBudget,
    expenses: ptExpenses,
    expenseSheets: ptExpenseSheets,
    tasks: ptTasks,
    agenda: ptAgenda,
    documents: ptDocuments,
    rates: ptRates,
    ai: ptAi,
    settings: ptSettings,
    notifications: ptNotifications,
    email: ptEmail,
    time: ptTime,
    products: ptProducts,
  },
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    ns: NAMESPACES as unknown as string[],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    detection: {
      // Orden: localStorage (preferencia previa) → navigator (Accept-Language).
      // El AuthContext sobrescribe con user.locale al login (fuente de verdad).
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'jr2-locale',
    },
    returnNull: false,
  })

export default i18n
